import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Zap, Bot, TrendingUp } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';

const floatingIcons = [
  { icon: Bot, x: '8%', y: '25%', delay: 0, color: '#0066FF' },
  { icon: Zap, x: '88%', y: '20%', delay: 0.5, color: '#00D4AA' },
  { icon: Sparkles, x: '82%', y: '65%', delay: 1, color: '#39FF14' },
  { icon: TrendingUp, x: '12%', y: '70%', delay: 1.5, color: '#00A8E8' },
];

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePosition({
        x: (e.clientX - rect.left) / rect.width - 0.5,
        y: (e.clientY - rect.top) / rect.height - 0.5,
      });
    };

    const hero = heroRef.current;
    hero?.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => hero?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 grid-pattern pointer-events-none" />

      {/* Floating Gradient Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 102, 255, 0.12) 0%, transparent 70%)',
          }}
          animate={{
            x: mousePosition.x * -25,
            y: mousePosition.y * -25,
          }}
          transition={{ type: 'spring', stiffness: 50, damping: 30 }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 212, 170, 0.1) 0%, transparent 70%)',
          }}
          animate={{
            x: mousePosition.x * 18,
            y: mousePosition.y * 18,
          }}
          transition={{ type: 'spring', stiffness: 50, damping: 30 }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(57, 255, 20, 0.08) 0%, transparent 70%)',
          }}
          animate={{
            x: mousePosition.x * -12,
            y: mousePosition.y * -12,
          }}
          transition={{ type: 'spring', stiffness: 50, damping: 30 }}
        />
      </div>

      {/* Floating Icons */}
      {floatingIcons.map((item, index) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={index}
            className="absolute pointer-events-none"
            style={{ left: item.x, top: item.y }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 0.5,
              scale: 1,
              y: [0, -12, 0],
            }}
            transition={{
              opacity: { duration: 0.8, delay: item.delay },
              scale: { duration: 0.5, delay: item.delay },
              y: { duration: 5, repeat: Infinity, ease: 'easeInOut', delay: item.delay },
            }}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center glass-card"
              style={{ boxShadow: `0 0 25px ${item.color}25` }}
            >
              <Icon className="w-6 h-6" style={{ color: item.color }} />
            </div>
          </motion.div>
        );
      })}

      {/* Content */}
      <motion.div
        className="relative z-10 max-w-5xl mx-auto text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Badge */}
        <motion.div variants={itemVariants} className="mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#00D4AA]/30">
            <Sparkles className="w-4 h-4 text-[#00D4AA]" />
            <span className="text-sm text-slate-600 font-medium">AI Visibility & SEO</span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={itemVariants}
          className="text-display font-extrabold mb-6 text-slate-900"
        >
          Buďte vidět
          <br />
          v <span className="text-gradient glow-text">AI vyhledávačích</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          variants={itemVariants}
          className="text-body text-lg sm:text-xl max-w-2xl mx-auto mb-10"
        >
          Optimalizujte svůj web pro ChatGPT, Perplexity a další AI nástroje.
          Získejte více návštěv z organického vyhledávání.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <Link to="/audit">
            <MagneticButton variant="primary">
              AI Visibility Audit
              <ArrowRight className="w-5 h-5" />
            </MagneticButton>
          </Link>
          <Link to="/model-spoluprace">
            <MagneticButton variant="outline">
              Model spolupráce
            </MagneticButton>
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap justify-center gap-4"
        >
          {[
            { value: '+127%', label: 'návštěvnost', color: '#00D4AA' },
            { value: '+340%', label: 'AI citace', color: '#0066FF' },
            { value: '48h', label: 'analýza', color: '#39FF14' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-3 px-5 py-3 rounded-2xl glass-card"
              whileHover={{ scale: 1.03, y: -2 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <div
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: stat.color, boxShadow: `0 0 8px ${stat.color}` }}
              />
              <div className="text-left">
                <span className="text-xl font-bold text-slate-800">{stat.value}</span>
                <span className="text-sm text-slate-500 ml-2">{stat.label}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#F0F4F8] to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
